import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import {
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  FileText,
  Star,
  Save,
  X,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Alert, AlertDescription } from '../../../components/ui/alert';

import { useClients } from '../Utils/useClients';
import { 
  clientFormSchema, 
  CLIENT_STATUSES, 
  CLIENT_PRIORITIES, 
  INDUSTRIES, 
  COMPANY_SIZES,
  sanitizeClientData 
} from '../Utils/clientsSchema';

export const ClientFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  onLoading,
  mode = 'create', // 'create' or 'edit'
  client = null,
  title,
  clientId // Will get this from props or context
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [submitError, setSubmitError] = useState('');
  
  const { createClient, updateClient } = useClients(clientId);
  
  const form = useForm({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      country: '',
      address: '',
      city: '',
      postal_code: '',
      tax_id: '',
      is_company: false,
      company_size: '',
      industry: '',
      website: '',
      notes: '',
      status: 'active',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      priority: 'medium',
      tags: []
    }
  });

  const { handleSubmit, reset, watch, formState: { isSubmitting, errors } } = form;
  const isCompany = watch('is_company');

  // Load client data when editing
  useEffect(() => {
    if (mode === 'edit' && client && isOpen) {
      const formData = {
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        country: client.country || '',
        address: client.address || '',
        city: client.city || '',
        postal_code: client.postal_code || '',
        tax_id: client.tax_id || '',
        is_company: Boolean(client.is_company),
        company_size: client.company_size || '',
        industry: client.industry || '',
        website: client.website || '',
        notes: client.notes || '',
        status: client.status || 'active',
        contact_person: client.contact_person || '',
        contact_email: client.contact_email || '',
        contact_phone: client.contact_phone || '',
        priority: client.priority || 'medium',
        tags: client.tags || []
      };
      reset(formData);
    } else if (mode === 'create' && isOpen) {
      reset({
        name: '',
        email: '',
        phone: '',
        country: '',
        address: '',
        city: '',
        postal_code: '',
        tax_id: '',
        is_company: true,
        company_size: '',
        industry: '',
        website: '',
        notes: '',
        status: 'active',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        priority: 'medium',
        tags: []
      });
    }
  }, [mode, client, isOpen, reset]);

  const onSubmit = async (data) => {
    try {
      setSubmitError('');
      onLoading?.(true);
      
      const cleanedData = sanitizeClientData(data);
      
      if (mode === 'create') {
        const result = await createClient(cleanedData);
        onSuccess?.(result.data);
      } else if (mode === 'edit' && client) {
        const clientCustomId = client.id || client._id || client.customClientId;
        if (!clientCustomId) {
          throw new Error('No se encontró el ID del cliente');
        }
        const result = await updateClient(clientCustomId, cleanedData);
        onSuccess?.(result.data);
      }
    } catch (error) {
      setSubmitError(error.message || 'Error al guardar el cliente');
      onLoading?.(false);
    }
  };

  const handleClose = () => {
    setSubmitError('');
    setActiveTab('basic');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-xl">
            {mode === 'create' ? (
              <User className="h-6 w-6 text-orange-500" />
            ) : (
              <Building2 className="h-6 w-6 text-orange-500" />
            )}
            <span>{title || (mode === 'create' ? 'Crear Nuevo Cliente' : 'Editar Cliente')}</span>
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Complete la información para crear un nuevo cliente'
              : 'Modifique la información del cliente'
            }
          </DialogDescription>
        </DialogHeader>

        {submitError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic" className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>Información Básica</span>
                </TabsTrigger>
                <TabsTrigger value="contact" className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>Contacto</span>
                </TabsTrigger>
                <TabsTrigger value="additional" className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Información Adicional</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Nombre *
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nombre del cliente" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="is_company"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded border border-slate-200/60 dark:border-slate-700/60 p-4 bg-white">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Es una empresa
                          </FormLabel>
                          <FormDescription>
                            Marque si este cliente es una empresa
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="ejemplo@correo.com" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Teléfono
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="+57 300 123 4567" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Estado
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                              <SelectValue placeholder="Seleccionar estado" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CLIENT_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Prioridad
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                              <SelectValue placeholder="Seleccionar prioridad" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CLIENT_PRIORITIES.map((priority) => (
                              <SelectItem key={priority.value} value={priority.value}>
                                {priority.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {isCompany && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200/60 dark:border-slate-700/60">
                    <FormField
                      control={form.control}
                      name="tax_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                            NIT / ID Fiscal
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="123456789-1" 
                              {...field} 
                              className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="company_size"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                            Tamaño de Empresa
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                                <SelectValue placeholder="Seleccionar tamaño" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {COMPANY_SIZES.map((size) => (
                                <SelectItem key={size.value} value={size.value}>
                                  {size.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                            Industria
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                                <SelectValue placeholder="Seleccionar industria" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {INDUSTRIES.map((industry) => (
                                <SelectItem key={industry.value} value={industry.value}>
                                  {industry.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                            Sitio Web
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="https://www.empresa.com" 
                              {...field} 
                              className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="contact" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="contact_person"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Persona de Contacto
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nombre del contacto" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Email de Contacto
                        </FormLabel>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="contacto@empresa.com" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Teléfono de Contacto
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="+57 300 123 4567" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          País
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Colombia" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Ciudad
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Bogotá" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                          Código Postal
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="110111" 
                            {...field} 
                            className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="md:col-span-2">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                            Dirección
                          </FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Dirección completa..." 
                              {...field} 
                              className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="additional" className="space-y-4">
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                        Notas Adicionales
                      </FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Información adicional sobre el cliente..." 
                          {...field}
                          rows={4}
                          className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
                        />
                      </FormControl>
                      <FormDescription>
                        Cualquier información adicional relevante sobre el cliente
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200/60 dark:border-slate-700/60">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isSubmitting 
                  ? (mode === 'create' ? 'Creando...' : 'Actualizando...') 
                  : (mode === 'create' ? 'Crear Cliente' : 'Actualizar Cliente')
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}; 